import { type ReactNode } from 'react';
import { alpha, Box, Button, Chip, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { DetailsLabelValue } from 'components/DetailsModal/DetailsLabelValue';
import { DetailsModalShell } from 'components/DetailsModal/DetailsModalShell';
import { formatDateTime } from 'constants/global.constants';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { FormButton } from 'theme/elements/Buttons';
import { FormCard } from 'theme/elements/Forms';
import type { HyperMonitoringBankSchemaField, HyperMonitoringOperationRecord } from 'types/hyper-monitoring.types';

type Props = {
	open: boolean;
	row: HyperMonitoringOperationRecord | null;
	detailsState: {
		data: Record<string, any> | null;
		loading: boolean;
	};
	actionLoading: boolean;
	onClose: () => void;
	onDocumentedClick: (row: HyperMonitoringOperationRecord) => void;
};

const summaryPanelSx = {
	display: 'grid',
	gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
	gap: 2.5,
};

const formatDisplayValue = (value: any) => {
	if (value === null || value === undefined || value === '') {
		return '—';
	}

	return value;
};

const SectionCard = ({ title, children }: { title: string; children: ReactNode }) => (
	<FormCard cardStyle={{ width: '100%' }}>
		<>
			{/* h4 (20px) matches wireframe .wcard-title; FormCard title uses pageTitle (24px). */}
			<Typography variant="h4" color="root.formTitle" sx={{ mb: 3, textAlign: 'start' }}>
				{title}
			</Typography>
			{children}
		</>
	</FormCard>
);

export const HyperMonitoringDetailsDialog = ({
	open,
	row,
	detailsState,
	actionLoading,
	onClose,
	onDocumentedClick,
}: Props) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const data = detailsState.data;
	const forestGreen = theme.palette.root.forestGreen;

	const titleRow = data || row;
	const showDocumentedButton = open && Boolean(row) && !detailsState.loading && data != null;

	let dialogBody: ReactNode = null;
	if (detailsState.loading) {
		dialogBody = (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 6, width: '100%' }}>
				<CircularProgress />
			</Box>
		);
	} else if (!data) {
		dialogBody = null;
	} else {
		dialogBody = (
			<Stack spacing={4} sx={{ width: '100%', WebkitFontSmoothing: 'antialiased', pt: 2 }}>
				<Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: 13 }}>
					{t('page.hyperMonitoring.manualDocumentation.intro')}
				</Typography>

				<SectionCard title={t('page.hyperMonitoring.manualDocumentation.section.openEvent')}>
					<Box sx={summaryPanelSx}>
						<Stack spacing={0.5}>
							<Typography variant="subtitleBold" color="text.primary">
								{t('page.hyperMonitoring.manualDocumentation.field.beneficiaryStatus')}
							</Typography>
							<Box>
								<Chip
									label={formatDisplayValue(data.beneficiaryStatus)}
									size="small"
									sx={{
										height: 24,
										color: forestGreen,
										bgcolor: alpha(forestGreen, 0.08),
										border: '1px solid',
										borderColor: 'root.mediumGreen',
										fontWeight: 500,
										'& .MuiChip-label': { px: 1 },
									}}
								/>
							</Box>
						</Stack>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.lastUpdatedAt')}
							value={formatDisplayValue(formatDateTime(data.lastUpdatedAt))}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.field.hyperFailureReceivedAt')}
							value={formatDisplayValue(formatDateTime(data.hyperFailureReceivedAt))}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.beneficiaryNickname')}
							value={formatDisplayValue(data.beneficiaryNickname)}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.sourceBoUser')}
							value={formatDisplayValue(data.sourceBoUser)}
						/>
					</Box>
				</SectionCard>

				<SectionCard title={t('page.hyperMonitoring.manualDocumentation.section.findCase')}>
					<Box sx={summaryPanelSx}>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.field.hyperAccountNumber')}
							value={formatDisplayValue(data.hyperAccountNumber)}
							breakAll
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.resendConfirm.companyHebrewName')}
							value={formatDisplayValue(data.companyHebrewName)}
						/>
					</Box>
				</SectionCard>

				<SectionCard title={t('page.hyperMonitoring.manualDocumentation.section.step1Contacts')}>
					<Box sx={summaryPanelSx}>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.beneficiaryCompanyName')}
							value={formatDisplayValue(data.beneficiaryName)}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.beneficiaryCountry')}
							value={formatDisplayValue(data.beneficiaryCountry)}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.bankAccountCountry')}
							value={formatDisplayValue(data.bankAccountCountry)}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.city')}
							value={formatDisplayValue(data.city)}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.street')}
							value={formatDisplayValue(data.street)}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.identityNumber')}
							value={formatDisplayValue(data.identityNumber)}
							breakAll
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.rejectionReason')}
							value={formatDisplayValue(data.rejectionReason)}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.openBeneficiaryId')}
							value={formatDisplayValue(data.openBeneficiaryId)}
							breakAll
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.wcCaseId')}
							value={formatDisplayValue(data.wcCaseId)}
							breakAll
						/>
					</Box>
				</SectionCard>

				<SectionCard title={t('page.hyperMonitoring.manualDocumentation.section.step2Bank')}>
					<Box sx={summaryPanelSx}>
						{data.bankSchemaFields.map((field: HyperMonitoringBankSchemaField) => (
							<DetailsLabelValue
								key={field.key}
								label={t(field.labelKey)}
								value={formatDisplayValue(field.value)}
								breakAll
							/>
						))}
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.accountHolder')}
							value={formatDisplayValue(data.accountHolder)}
						/>
						<DetailsLabelValue
							label={t('page.hyperMonitoring.manualDocumentation.field.currency')}
							value={formatDisplayValue(data.currency)}
						/>
					</Box>
				</SectionCard>
			</Stack>
		);
	}

	return (
		<DetailsModalShell
			open={open}
			onClose={onClose}
			ariaLabelledBy="hyper-monitoring-details-dialog-title"
			closeLabel={t('label.close')}
			title={t('page.hyperMonitoring.manualDocumentation.title')}
			subtitle={
				titleRow ? (
					<>
						{t('page.hyperMonitoring.manualDocumentation.subtitlePrefix')}{' '}
						<Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
							{titleRow.beneficiaryName}
						</Box>
						{' · '}
						{titleRow.companyHebrewName}
					</>
				) : null
			}
			scroll="paper"
			footer={
				<Stack direction="row" justifyContent="flex-end" width="100%" flexWrap="wrap" spacing={1.25}>
					<FormButton>
						<Button variant="outlined" color="inherit" onClick={onClose}>
							{t('label.close')}
						</Button>
					</FormButton>
					{showDocumentedButton && row ? (
						<FormButton>
							<Button variant="contained" disabled={actionLoading} onClick={() => onDocumentedClick(row)}>
								{t('page.pendingRequests.field.approve.documentedOnHyper')}
							</Button>
						</FormButton>
					) : null}
				</Stack>
			}
		>
			{dialogBody}
		</DetailsModalShell>
	);
};
